<template>
  <div :class="containerClass">
    <v-layout row wrap>
      <v-flex hidden-md-and-up>
        <SubHeader>Date</SubHeader>
      </v-flex>

      <v-flex xs12>
        <v-menu xs12 class="scheduleform--calendar-date">
          <v-text-field
            :value="computedDateFormatted"
            append-icon="event"
            class="date-picker-text-box hidden-md-and-up"
            flat
            outline
            readonly
            slot="activator"
            solo
            @click="toggleOpenCalendar"
            @click:append="toggleOpenCalendar" />
        </v-menu>

        <div class="scheduleform--calendar-container">
          <v-date-picker
            :allowed-dates="generateCalendarDateAllowed"
            :events="generateCalendarDateColor"
            :min="today"
            :header-date-format="generateMonthFormat"
            :picker-date.sync="pickerDate"
            :weekday-format="generateWeekdayFormat"
            :value="filterDate"
            full-width
            no-title
            @input="selectDate" />
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { Mixins } from '@homegauge/vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import SubHeader from '@/components/common/SubHeader.vue';
import DateTimeConstants from '@/constants/date-time';

const { DateTime } = Mixins;

export default {
  components: {
    SubHeader,
  },
  data() {
    return {
      calendarOpen: false,
      today: new Date().toISOString(),
    };
  },
  mixins: [DateTime],
  computed: {
    ...mapGetters('Schedule', [
      'filteredDates',
    ]),
    ...mapState('Schedule', {
      filterDate: state => state.filters.date,
    }),
    pickerDate: {
      get() {
        return this.filterMonth;
      },
      set(month) {
        this.setMonthFilter(month);
      },
    },
    computedDateFormatted() {
      return this.longDate(this.filterDate);
    },
    containerClass() {
      return {
        'scheduleform--calendar': true,
        calendarOpen: this.calendarOpen,
      };
    },
  },
  methods: {
    ...mapActions('Schedule', [
      'setMonthFilter',
    ]),
    generateCalendarDateAllowed(date) {
      return !!(this.filteredDates
        && this.filteredDates.includes(date));
    },
    generateCalendarDateColor(date) {
      return (this.filteredDates
        && this.filteredDates.includes(date))
        ? 'blue'
        : '';
    },
    generateMonthFormat(date) {
      const month = new Date(date).getUTCMonth();
      return DateTimeConstants.months[month].full;
    },
    generateWeekdayFormat(date) {
      const day = new Date(date).getUTCDay();
      return DateTimeConstants.weekdays[day].abbreviated;
    },
    toggleOpenCalendar() {
      this.calendarOpen = !this.calendarOpen;
    },
    selectDate(date) {
      this.calendarOpen = false;
      this.$emit('input', date);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/mixins/breakpoints';
@import '../styles/vars/calendar';

.scheduleform--calendar {
  position: relative;

  ::v-deep .v-picker {
    padding: $calendar-picker-padding;

    &.v-card {
      border: $calendar-picker-border-size solid $calendar-picker-border-color;
      border-radius: $calendar-picker-border-radius;
      box-shadow: $calendar-picker-box-shadow;
    }
  }

  ::v-deep .v-date-picker-header {
    padding: 0.3rem 0;

    .v-date-picker-header__value {
      button {
        color: $calendar-month-color;
        font-size: $calendar-month-font-size;
        font-weight: $calendar-month-font-weight;
        text-transform: uppercase;
      }
    }

    .v-icon {
      color: $calendar-day-available-color;
      font-size: 2.6rem;
    }
  }

  ::v-deep .v-date-picker-table {
    // vuetify defines this as a static value so we need to reset it
    // set !important to work with IE and Safari's CSS rendering order
    height: auto !important;

    thead {
      th {
        color: $calendar-day-header-color;
        font-size: $calendar-day-header-font-size;
        font-weight: $calendar-day-header-font-weight;
        letter-spacing: 0.14rem;
        padding: 20px 0 18px;
        text-transform: uppercase;
      }
    }

    tbody {
      td {
        padding: 0.3rem 0;

        .v-btn {
          color: $calendar-day-available-color;
          font-size: $calendar-day-font-size;
          font-weight: $calendar-day-font-weight;
          height: $calendar-day-size;
          line-height: 1;
          width: $calendar-day-size;

          &.v-btn--active {
            // Vuetify uses !important, so we have to if we want to override it
            background-color: $calendar-day-selected-background !important;
            color: $calendar-day-selected-color;
          }

          &.v-btn--disabled {
            color: $calendar-day-unavailable-color !important;
          }

          &.v-btn--outline {
            border: $calendar-day-border-width solid $calendar-day-current-border;
          }
        }
      }
    }

    .v-date-picker-table__events {
      display: none;
    }
  }

  &-date {
    cursor: pointer;
    width: 100%;
  }

  .date-picker-text-box.v-text-field--solo {
    ::v-deep .v-input__control {
      .v-input__slot {
        border-radius: 5px;
      }
    }
  }
}

@include responsive-breakpoint(sm) {
  .scheduleform--calendar {
    &-container {
      opacity: 0;
      position: absolute;
      top: 40px;
      transition: 0.2s all;
      z-index: -1;
    }

    ::v-deep .v-picker {
      padding: 4px 0;

      &.v-card {
        border: 1px solid #838383;
        border-radius: 4px;
      }
    }

    ::v-deep .v-date-picker-header {
      .v-date-picker-header__value {
        button {
          font-size: 1.4rem;
        }
      }

      .v-icon {
        font-size: 2.5rem;
      }
    }

    ::v-deep .v-date-picker-table {
      thead {
        th {
          font-size: 1rem;
          padding: 6px 0 0;
        }
      }

      tbody {
        td {
          padding: 2px 0;

          .v-btn {
            font-size: 1.1rem;
            height: 32px;
            width: 32px;
          }
        }
      }
    }

    &.calendarOpen {
      padding-bottom: 100px;

      .scheduleform--calendar-container {
        opacity: 1;
        top: 60px;
        z-index: 2;
      }
    }
  }
}
</style>
